import { useEffect } from "react"
import { useState } from "react"
import { useCallback } from "react"
import { useMemo } from "react"
import Link from "@/src/components/Link"
import Image from "@/src/components/Image"
import { useLocation } from "react-router-dom"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { LogOut, Menu, X, Clock, AlertTriangle, CheckCircle, ChevronDown, ChevronRight } from "lucide-react"
import {
  MoneyIcon,
} from "./sidebar-icons"
import { useAuth } from "@/src/contexts/AuthContext"
import { customerNavigation } from "@/src/components/dashboard/sidebar-navigation"
import { usePackageStatus } from "@/hooks/usePackageStatus"
import { useKyc } from "@/src/contexts/KycContext"
import { Badge } from "@/components/ui/badge"
// import { differenceInDays } from "date-fns"

interface SidebarProps {
  className?: string
}

const SIDEBAR_ACTIVE_SECTION_STORAGE_KEY = "customer-sidebar-active-section"

const SECTION_ROUTE_PREFIXES: Record<string, string[]> = {
  "จัดการร้าน": ["/dashboard"],
  "API Connect": ["/api-connect"],
  "ประวัติการใช้งาน": ["/history"],
  "การตั้งค่า": ["/settings"],
}

const getSectionFromPathname = (currentPathname: string) => {
  for (const [sectionTitle, prefixes] of Object.entries(SECTION_ROUTE_PREFIXES)) {
    if (prefixes.some((prefix) => currentPathname === prefix || currentPathname.startsWith(`${prefix}/`))) {
      return sectionTitle
    }
  }

  return null
}

interface SidebarContentProps {
  allSections: typeof customerNavigation
  pathname: string
  user: ReturnType<typeof useAuth>["user"]
  packageStatus: ReturnType<typeof usePackageStatus>["packageStatus"]
  packageLoading: boolean
  kycStatus: ReturnType<typeof useKyc>["kycStatus"]
  displayCredits: number
  expandedSectionTitle: string | null
  onToggleSection: (sectionTitle: string) => void
  onNavigate: () => void
  onLogout: () => void
}

function SidebarContent({
  allSections,
  pathname,
  user,
  packageStatus,
  packageLoading,
  kycStatus,
  displayCredits,
  expandedSectionTitle,
  onToggleSection,
  onNavigate,
  onLogout,
}: SidebarContentProps) {
  return (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-4 lg:p-6 lg:px-3.5 lg:py-3">
        <div className="flex items-center justify-center mb-4">
          <Image src="/images/app-logo-2.svg" alt="THAISLIPCHECK" width={200} height={40} className="w-auto" />
        </div>

        {/* Store Quick Summary with Package Status */}
        <div className="rounded-lg border border-gray-200 p-3 lg:p-4 mb-3 bg-white">
          <div className="flex items-center space-x-3 mb-2">
            <Image src="/icons/icon-store.svg" alt="Store" width={40} height={40} className="w-10 h-10 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm lg:text-base font-semibold text-gray-900 truncate">{user?.shopName || "ร้านของฉัน"}</p>
              <p className="text-xs lg:text-sm text-gray-500 capitalize mt-0.5">
                {packageStatus?.name || user?.packageName || user?.packageCode || "starter"}
              </p>
              {!packageLoading && (
                <div className="mt-1.5">
                  {kycStatus?.status === "pending" ? (
                    <Badge variant="outline" className="text-xs px-1.5 py-0 bg-yellow-50 text-yellow-700 border-yellow-200">
                      <Clock className="w-3 h-3 mr-1" />
                      รออนุมัติ
                    </Badge>
                  ) : packageStatus ? (
                    <>
                      {packageStatus.status === "active" && !packageStatus.cancelled_at && (
                        <Badge variant="outline" className="text-xs px-1.5 py-0 bg-green-50 text-green-700 border-green-200">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          ใช้งาน
                        </Badge>
                      )}
                      {packageStatus.status === "cancelled_active" && packageStatus.days_remaining !== null && (
                        <Badge variant="outline" className="text-xs px-1.5 py-0 bg-yellow-50 text-yellow-700 border-yellow-200">
                          <Clock className="w-3 h-3 mr-1" />
                          {packageStatus.days_remaining} วัน
                        </Badge>
                      )}
                      {packageStatus.status === "expired" && (
                        <Badge variant="outline" className="text-xs px-1.5 py-0 bg-red-50 text-red-700 border-red-200">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          หมดอายุ
                        </Badge>
                      )}
                    </>
                  ) : kycStatus?.status === "approved" ? (
                    <Badge variant="outline" className="text-xs px-1.5 py-0 bg-green-50 text-green-700 border-green-200">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      ใช้งาน
                    </Badge>
                  ) : null}
                </div>
              )}
            </div>
          </div>

          {/* Package Status Alert for Cancelled or Expiring Soon */}
          {packageStatus && !packageLoading && (
            <>
              {packageStatus.status === "cancelled_active" && packageStatus.days_remaining !== null && (
                <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
                  <div className="flex items-start gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium">Package ถูกยกเลิก</p>
                      <p className="text-yellow-700 mt-0.5">
                        ใช้งานได้ถึง{" "}
                        {new Date(packageStatus.expires_at!).toLocaleDateString("th-TH", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {packageStatus.status === "active" && packageStatus.is_expiring_soon && !packageStatus.cancelled_at && (
                <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded text-xs text-orange-800">
                  <div className="flex items-start gap-1.5">
                    <Clock className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium">ใกล้หมดอายุ</p>
                      <p className="text-orange-700 mt-0.5">เหลือ {packageStatus.days_remaining} วัน</p>
                    </div>
                  </div>
                </div>
              )}

              {packageStatus.status === "expired" && (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800">
                  <div className="flex items-start gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium">Package หมดอายุ</p>
                      <p className="text-red-700 mt-0.5">กรุณาต่ออายุเพื่อใช้งานต่อ</p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/*<div className="flex items-center justify-center space-x-2">
          <Image src="/icons/icon-transfer.svg" alt="Change" width={32} height={32} className="w-6 h-6 lg:w-8 lg:h-8" />
          <span className="text-sm lg:text-base text-gray-700 font-medium">เปลี่ยนร้าน</span>
        </div>*/}
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto py-2 lg:py-4">
        {allSections.map((section) => {
          const isExpanded = expandedSectionTitle === section.title

          return (
          <div key={section.title} className={cn(isExpanded ? "mb-4 lg:mb-5" : "mb-0.5 lg:mb-1")}>
            <button
              type="button"
              onClick={() => onToggleSection(section.title)}
              className={cn(
                "w-full flex items-center justify-between px-4 lg:px-6 py-1 lg:py-1.5 text-xs lg:text-sm font-semibold uppercase tracking-wider rounded-md transition-colors",
                isExpanded ? "text-brand-primary bg-brand-primary/10" : "text-gray-500 hover:bg-gray-100",
              )}
            >
              <span>{section.title}</span>
              {isExpanded ? (
                <ChevronDown className="h-4 w-4 flex-shrink-0" />
              ) : (
                <ChevronRight className="h-4 w-4 flex-shrink-0" />
              )}
            </button>

            {isExpanded && (
              <nav className="space-y-1 px-2 lg:px-3 mt-1">
                {section.items.map((item) => {
                  const isActive = pathname === item.href
                  const IconComponent = item.icon

                  return (
                    <Link
                      key={item.name}
                      href={item.href}
                      className={cn(
                        "flex items-center px-2 lg:px-3 py-2 text-xs lg:text-sm font-medium rounded-lg transition-colors",
                        isActive ? "bg-brand-primary text-white" : "text-gray-700 hover:bg-gray-100 hover:text-gray-900",
                      )}
                      onClick={onNavigate}
                    >
                      {item.isCustom ? (
                        <IconComponent className="mr-2 lg:mr-3 h-4 w-4 lg:h-5 lg:w-5 flex-shrink-0" isActive={isActive} />
                      ) : (
                        <IconComponent className="mr-2 lg:mr-3 h-4 w-4 lg:h-5 lg:w-5 flex-shrink-0" />
                      )}
                      <span className="truncate">{item.name}</span>
                    </Link>
                  )
                })}
              </nav>
            )}
          </div>
          )
        })}
      </div>

      {/* Credit Balance and Topup Button */}
      <div className="p-3 lg:p-4 border-t border-gray-200 space-y-3">
        {/* Topup Button */}
        <Link href="/user/add-credit" className="block">
          <Button type="button" className="w-full bg-brand-red hover:bg-red-600 text-white text-base font-normal py-2">
            <MoneyIcon />
            เติมเครดิต
          </Button>
        </Link>
      </div>

      {/* User Profile */}
      <div className="p-3 lg:p-4">
        <div className="rounded-lg p-3 lg:p-4 bg-background border lg:px-3 lg:py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 lg:space-x-3 min-w-0">
              <div className="w-8 h-8 lg:w-10 lg:h-10 bg-brand-primary rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white text-sm lg:text-base font-medium">
                  {user?.shopName?.charAt(0) || "A"}
                </span>
              </div>
              <div className="min-w-0">
                <p className="text-xs lg:text-sm font-medium text-gray-900 truncate">{user?.shopName || "ผู้ใช้งาน"}</p>
                <p className="text-xs text-brand-red font-medium">
                  {displayCredits !== undefined ? displayCredits.toLocaleString() : "0"} เครดิต
                </p>
              </div>
            </div>
            <Button variant="ghost" size="sm" className="text-gray-500 hover:text-gray-700 p-1 lg:p-2" onClick={onLogout}>
              <LogOut className="h-3 w-3 lg:h-4 lg:w-4 text-brand-red" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export function Sidebar({ className }: SidebarProps) {
  const location = useLocation()
  const pathname = location.pathname
  const [isMobileOpen, setIsMobileOpen] = useState(false)
  const [displayCredits, setDisplayCredits] = useState<number>(0) // Local state for credit display only
  const [lastOpenSection, setLastOpenSection] = useState<string | null>(null)
  const { user, checkAccess, logout, registerDisplayCreditsCallback, refreshCredits } = useAuth()
  const { packageStatus, loading: packageLoading } = usePackageStatus()
  const { kycStatus } = useKyc()

  useEffect(() => {
    const storedSection = sessionStorage.getItem(SIDEBAR_ACTIVE_SECTION_STORAGE_KEY)
    if (storedSection) {
      setLastOpenSection(storedSection)
    }
  }, [])

  // Register callback once on mount and seed with cached value
  useEffect(() => {
    registerDisplayCreditsCallback(setDisplayCredits)
    if (user?.credits !== undefined) {
      setDisplayCredits(user.credits)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Sync displayCredits when user.credits changes (handles page refresh race condition —
  // user state may resolve after sidebar mounts, so we reactively update here)
  useEffect(() => {
    if (user?.credits !== undefined) {
      setDisplayCredits(user.credits)
    }
  }, [user?.credits])

  // Fetch fresh credit balance from API on mount and on every page navigation
  useEffect(() => {
    refreshCredits()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  // Filter navigation items based on user permissions
  const filteredNavigation = useMemo(() => {
    return customerNavigation
      .map(section => ({
        ...section,
        items: section.items.filter(item => {
          // Check if user has required permissions for this menu item
          const hasAccess = checkAccess({
            permissions: item.requiredPermissions,
            permissionsAll: item.requiredPermissionsAll,
            roles: item.requiredRole
          })
          return hasAccess
        })
      }))
      .filter(section => section.items.length > 0) // Remove empty sections
  }, [checkAccess])

  const routeMatchedSection = getSectionFromPathname(pathname)
  const hasSectionAccess = useCallback((sectionTitle: string | null) => {
    if (!sectionTitle) {
      return false
    }
    return filteredNavigation.some((section) => section.title === sectionTitle)
  }, [filteredNavigation])

  const activeSectionTitle = (() => {
    if (hasSectionAccess(lastOpenSection)) {
      return lastOpenSection
    }

    if (hasSectionAccess(routeMatchedSection)) {
      return routeMatchedSection
    }

    return filteredNavigation[0]?.title || null
  })()

  useEffect(() => {
    if (routeMatchedSection && hasSectionAccess(routeMatchedSection)) {
      setLastOpenSection(routeMatchedSection)
      sessionStorage.setItem(SIDEBAR_ACTIVE_SECTION_STORAGE_KEY, routeMatchedSection)
    }
  }, [pathname, routeMatchedSection, hasSectionAccess])

  useEffect(() => {
    if (activeSectionTitle && activeSectionTitle !== lastOpenSection) {
      setLastOpenSection(activeSectionTitle)
      sessionStorage.setItem(SIDEBAR_ACTIVE_SECTION_STORAGE_KEY, activeSectionTitle)
    }
  }, [activeSectionTitle, lastOpenSection])

  useEffect(() => {
    setIsMobileOpen(false)
  }, [pathname])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const sidebar = document.getElementById("mobile-sidebar")
      const menuButton = document.getElementById("mobile-menu-button")

      if (
        isMobileOpen &&
        sidebar &&
        !sidebar.contains(event.target as Node) &&
        menuButton &&
        !menuButton.contains(event.target as Node)
      ) {
        setIsMobileOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [isMobileOpen])

  return (
    <>
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <Button
          id="mobile-menu-button"
          variant="outline"
          size="sm"
          onClick={() => setIsMobileOpen(!isMobileOpen)}
          className="bg-white shadow-md"
        >
          {isMobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
        </Button>
      </div>

      {/* Mobile sidebar overlay */}
      {isMobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black bg-opacity-50 transition-opacity"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Mobile sidebar */}
      <div
        id="mobile-sidebar"
        className={cn(
          "lg:hidden fixed inset-y-0 left-0 z-40 w-64 bg-white shadow-xl transform transition-transform duration-300 ease-in-out",
          isMobileOpen ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <SidebarContent
          allSections={filteredNavigation}
          pathname={pathname}
          user={user}
          packageStatus={packageStatus}
          packageLoading={packageLoading}
          kycStatus={kycStatus}
          displayCredits={displayCredits}
          expandedSectionTitle={activeSectionTitle}
          onToggleSection={(sectionTitle) => {
            const nextSection = sectionTitle === activeSectionTitle ? null : sectionTitle
            setLastOpenSection(nextSection)
            if (nextSection) {
              sessionStorage.setItem(SIDEBAR_ACTIVE_SECTION_STORAGE_KEY, nextSection)
            } else {
              sessionStorage.removeItem(SIDEBAR_ACTIVE_SECTION_STORAGE_KEY)
            }
          }}
          onNavigate={() => setIsMobileOpen(false)}
          onLogout={logout}
        />
      </div>

      {/* Desktop sidebar */}
      <div
        className={cn(
          "hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 bg-white border-r border-gray-200",
          className,
        )}
      >
        <SidebarContent
          allSections={filteredNavigation}
          pathname={pathname}
          user={user}
          packageStatus={packageStatus}
          packageLoading={packageLoading}
          kycStatus={kycStatus}
          displayCredits={displayCredits}
          expandedSectionTitle={activeSectionTitle}
          onToggleSection={(sectionTitle) => {
            const nextSection = sectionTitle === activeSectionTitle ? null : sectionTitle
            setLastOpenSection(nextSection)
            if (nextSection) {
              sessionStorage.setItem(SIDEBAR_ACTIVE_SECTION_STORAGE_KEY, nextSection)
            } else {
              sessionStorage.removeItem(SIDEBAR_ACTIVE_SECTION_STORAGE_KEY)
            }
          }}
          onNavigate={() => setIsMobileOpen(false)}
          onLogout={logout}
        />
      </div>
    </>
  )
}
